import * as Yup from 'yup';
import { isValid, isBefore, isAfter, parse, addYears } from 'date-fns';
import { cpf } from 'cpf-cnpj-validator';

function validateDocument(document) {
  if (document) {
    const valid = cpf.isValid(document);

    return valid;
  }
  return true;
}

function whiteSpace(value) {
  if (value) {
    const onlySpace = value.replace(/\s/g, '');
    return !!onlySpace;
  }

  return true;
}

function validateDate(value) {
  if (value) {
    const [day, month, year] = value.split('/');

    if (!Number(year) && !Number(month) && !Number(day)) {
      return false;
    }

    const date = parse(`${Number(year)}-${Number(month)}-${Number(day)}`, 'yyyy-MM-dd', new Date());

    const valid = isValid(date);

    if (!valid) {
      return false;
    }

    const isGreatter = isBefore(date, new Date());

    return isGreatter;
  }

  return true;
}

function testPhone(value) {
  if (value) {
    const onlyNumber = value.replace(/\D/g, '');

    return onlyNumber.length === 11 || onlyNumber.length === 10;
  }

  return true;
}

function validateFutureDate(value) {
  if (value) {
    const [day, month, year] = value.split('/');

    if (!Number(year) && !Number(month) && !Number(day)) {
      return false;
    }

    const date = parse(`${Number(year)}-${Number(month)}-${Number(day)}`, 'yyyy-MM-dd', new Date());

    if (!isValid(date)) {
      return false;
    }

    return isAfter(date, new Date());
  }
  return true;
}

function expireDate(value, time) {
  if (value) {
    const maxDate = addYears(new Date(), time);

    const [day, month, year] = value.split('/');

    if (!Number(year) && !Number(month) && !Number(day)) {
      return false;
    }

    const date = parse(`${Number(year)}-${Number(month)}-${Number(day)}`, 'yyyy-MM-dd', new Date());

    if (!date) {
      return false;
    }

    return isBefore(date, maxDate);
  }
  return true;
}

export const validator = Yup.object().shape(
  {
    is_international: Yup.boolean(),
    cnh_is_foreign: Yup.boolean(),
    born_city_id: Yup.number().required('Campo obrigatório').typeError('Campo obrigatório'),
    cgccpf: Yup.string()
      .required('Campo obrigatório')
      .when('is_international', {
        is: false,
        then: (schema) =>
          schema.test('invalid-document', 'Documento inválido', (value) => validateDocument(value)),
      }),
    social_name: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    birthdate: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', (value) => validateDate(value)),
    rg: Yup.string().when('is_international', {
      is: false,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
      otherwise: (schema) =>
        schema.nullable().test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    }),
    rg_emissor: Yup.string().when('is_international', {
      is: false,
      then: (schema) =>
        schema
          .required('Campo obrigatório')
          .test('rg-emissor-white-space', 'Campo inválido', (value) => whiteSpace(value)),
      otherwise: (schema) =>
        schema
          .nullable()
          .test('rg-emissor-white-space', 'Campo inválido', (value) => whiteSpace(value)),
    }),
    cnh_type: Yup.string()
      .nullable()
      .when('cnh_is_foreign', {
        is: false,
        then: (schema) => schema.required('Campo obrigatório').typeError('Campo obrigatório'),
        otherwise: (schema) => schema.nullable(),
      }),
    rg_dispatch_province: Yup.number()
      .nullable()
      .when('cnh_is_foreign', {
        is: false,
        then: (schema) => schema.required('Campo obrigatório').typeError('Campo obrigatório'),
        otherwise: (schema) => schema.nullable().typeError('Campo obrigatório'),
      }),
    rg_dispatch_date: Yup.string()
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', (value) => validateDate(value)),
    mothers_name: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    father_name: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    civil_state_id: Yup.number().required('Campo obrigatório').typeError('Campo obrigatório'),
    gender_id: Yup.number().required('Campo obrigatório').typeError('Campo obrigatório'),
    phone: Yup.string()
      .required('Campo obrigatório')
      .test('valid-phone', 'Telefone inválido', (value) => testPhone(value)),
    email: Yup.string().required('Campo obrigatório').email('E-mail inválido'),
    cnh_number: Yup.string()
      .required('Campo obrigatório')
      .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    cnh_key: Yup.string().test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    cnh_dispatch_city: Yup.number()
      .nullable()
      .when(['is_international', 'cnh_is_foreign'], {
        is: (isIntl, isForeign) => !isIntl && !isForeign,
        then: (schema) => schema.required('Campo obrigatório').typeError('Campo obrigatório'),
        otherwise: (schema) => schema.nullable().typeError('Campo obrigatório'),
      }),
    first_cnh_date: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', (value) => validateDate(value)),
    cnh_dispatch_date: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('invalid-date', 'Data inválida', (value) => validateDate(value)),
    cnh_exp_date: Yup.string()
      .required('Campo obrigatório')
      .typeError('Data inválida')
      .test('future-date', 'CNH vencida', (value) => validateFutureDate(value))
      .test('exp-date', 'Válidade máxima 10 anos', (value) => expireDate(value, 10)),
    hasMopp: Yup.boolean(),
    mopp_description: Yup.string()
      .default(null)
      .when('hasMopp', {
        is: (value) => value,
        then: (schema) => schema,
      })
      .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
    mopp_expiration: Yup.string()
      .default(null)
      .when('hasMopp', {
        is: (value) => value,
        then: (schema) => schema,
      })
      .test('exp-date', 'Válidade máxima 5 anos', (value) => expireDate(value, 5)),
    mopp_date_init: Yup.string()
      .default(null)
      .when('hasMopp', {
        is: (value) => value,
        then: (schema) => schema,
      })
      .test('invalid-date', 'Data inválida', (value) => validateDate(value)),
    selectedTagList: Yup.array()
      .of(
        Yup.object({
          value: Yup.number().typeError('Campo inválido'),
        }),
      )
      .default([]),
    pispasep: Yup.string()
      .nullable()
      .default(null)
      .when('is_autonomous', {
        is: true,
        then: (schema) =>
          schema
            .required('Campo obrigatório')
            .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
        otherwise: (schema) =>
          schema
            .nullable()
            .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
      }),
    paymentMethod: Yup.object()
      .required('Verifique os campos obrigatórios')
      .typeError('Verifique os campos obrigatórios'),
    addressObject: Yup.object().shape({
      address: Yup.string()
        .required('Campo obrigatório')
        .typeError('Campo obrigatório')
        .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
      numberless: Yup.boolean(),
      number: Yup.string()
        .nullable()
        .when('numberless', {
          is: true,
          then: (schema) => schema.notRequired(),
          otherwise: (schema) =>
            schema.required('Campo obrigatório').typeError('Campo obrigatório'),
        }),
      neighborhood: Yup.string()
        .required('Campo obrigatório')
        .typeError('Campo obrigatório')
        .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
      zip_code: Yup.string().required('Campo obrigatório').typeError('Campo obrigatório'),
      city_name: Yup.string().required('Campo obrigatório').typeError('Campo obrigatório'),
      uf: Yup.string().required('Campo obrigatório').typeError('Campo obrigatório'),
      country_id: Yup.number().required('Campo obrigatório').typeError('Campo obrigatório'),
    }),
  },

  ['mopp_description', 'mopp_expiration', 'mopp_date_init'],
);

export const validatorPhones = Yup.object().shape({
  phone: Yup.string()
    .required('Campo obrigatório')
    .test('valid-phone', 'Telefone inválido', (value) => testPhone(value)),
  name: Yup.string()
    .required('Campo obrigatório')
    .test('white-space-field', 'Campo inválido', (value) => whiteSpace(value)),
});

export const ValidatorPaymentsCard = Yup.array().of(
  Yup.object().shape({
    corporation: Yup.object().typeError('Campo obrigatório'),
    card_number: Yup.string(),
    due_date: Yup.string(),
  }),
);
